"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { Citation } from "../types";

interface CitationListItemProps {
  citation: Citation;
  /** Optional citation ID search parameter name (e.g., 'citationId') */
  citationIdParam?: string;
  /** Optional callback to handle citation click, overrides default navigation behavior */
  onCitationClick?: (citation: Citation) => void;
}

/**
 * Component to render a single citation item in the popover list
 */
export const CitationListItem = ({ citation, citationIdParam, onCitationClick }: CitationListItemProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const uniquePages = citation.pages ? [...new Set(citation.pages)].sort((a, b) => a - b) : [];
  const pageText =
    uniquePages.length === 0
      ? null
      : uniquePages.length === 1
        ? `pg ${uniquePages[0]}`
        : uniquePages.length === 2
          ? `pg ${uniquePages[0]} & ${uniquePages[1]}`
          : `pg ${uniquePages[0]}-${uniquePages[uniquePages.length - 1]}`;

  const sectionText = citation.sectionId ? `section ${citation.sectionId}` : null;
  const metadataParts = [pageText, sectionText].filter(Boolean);
  const metadataText = metadataParts.length > 0 ? metadataParts.join(", ") : null;

  const displayFileName = citation.fileName.length > 70 ? `${citation.fileName.slice(0, 70)}...` : citation.fileName;

  const handleClick = async () => {
    // If custom click handler is provided, use it instead of default navigation
    if (onCitationClick) {
      onCitationClick(citation);
      return;
    }

    // Dynamically import to avoid bundle size increase for basic usage
    const { getCitationHighlights, getEncodedHighlightsWithPage } = await import("../utils/citationHelpers");

    const highlights = getCitationHighlights(citation);
    const { encodedHighlights, page: highlightsPage } = getEncodedHighlightsWithPage(highlights);
    const citationId = citationIdParam ? `${citation.fileId}-${citation.citationIndex}` : undefined;

    // Determine target page: use highlights page, first citation page, or fallback to page 1
    const targetPage = highlightsPage ?? citation.pages?.[0] ?? 1;

    // Check if we're already at the crunches URL for this file
    const isOnCrunchesPage = pathname === `/crunches/${citation.fileId}`;

    if (isOnCrunchesPage) {
      // We're already on this file's page, so just update the URL params (scroll and highlight)
      const params = new URLSearchParams(searchParams);
      const currentHighlight = params.get("highlight");
      const currentCitationId = citationIdParam ? params.get(citationIdParam) : undefined;

      // If the same citation is already active (both highlight and citation ID match), remove it (toggle off)
      if (
        currentHighlight === encodedHighlights &&
        (!citationIdParam || currentCitationId === citationId) &&
        encodedHighlights
      ) {
        params.delete("highlight");
        if (citationIdParam) {
          params.delete(citationIdParam);
        }
        params.delete("page");
      } else {
        // Always set page, even if we don't have highlights
        params.set("page", targetPage.toString());

        // Add highlight and citation ID parameters if we have encoded highlights
        if (encodedHighlights) {
          params.set("highlight", encodedHighlights);
          if (citationIdParam && citationId) {
            params.set(citationIdParam, citationId);
          }
        } else {
          params.delete("highlight");
          if (citationIdParam) {
            params.delete(citationIdParam);
          }
        }
      }

      const url = `${pathname}?${params.toString()}`;
      router.push(url, { scroll: false });
    } else {
      // Navigate to a different file, open in new tab
      const params = new URLSearchParams();

      // Always set page, even if we don't have highlights
      params.set("page", targetPage.toString());

      // Add highlight and citation ID parameters if we have encoded highlights
      if (encodedHighlights) {
        params.set("highlight", encodedHighlights);
        if (citationIdParam && citationId) {
          params.set(citationIdParam, citationId);
        }
      }

      const url = `/crunches/${citation.fileId}?${params.toString()}`;

      // Open in new tab
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <button
      type="button"
      className="flex flex-1 cursor-pointer flex-col items-start justify-start text-left"
      onClick={handleClick}
    >
      <div className="justify-center self-stretch font-['Inter'] font-normal text-icon-neutral-onInverse-usual text-sm leading-5">
        {displayFileName}
      </div>
      {metadataText && (
        <div className="justify-center self-stretch font-['Inter'] font-normal text-icon-neutral-onInverse-dimmed text-xs leading-4">
          ({metadataText})
        </div>
      )}
    </button>
  );
};
